import { PublicKey } from "@solana/web3.js";
import {
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
} from "@solana/spl-token";
import bs58 from "bs58";
import {
  LOCK_TYPE_CLIFFS,
  LOCK_TYPE_TIME,
  claimableRaw,
  decodeCliffSchedule,
  deriveCliffSchedulePda,
  nextCliffTime,
} from "./cliffs";
import {
  LOCK_RECEIVERS_DISC,
  decodeLockReceivers,
  deriveLockReceiversPda,
  receiverShareRaw,
} from "./receivers";

/** Anchor account discriminator for LockRecord */
export const LOCK_RECORD_DISC = Buffer.from([
  157, 145, 17, 26, 171, 35, 61, 131,
]);

const OLD_LOCK_RECORD_LEN = 89; // pre price-lock layout
const NEW_LOCK_RECORD_LEN = 162; // disc + LockRecord::LEN body

export function deriveLockRecordPda(programId, ownerPubkey, lpMintPubkey) {
  const program =
    programId instanceof PublicKey ? programId : new PublicKey(programId);
  const owner =
    ownerPubkey instanceof PublicKey ? ownerPubkey : new PublicKey(ownerPubkey);
  const mint =
    lpMintPubkey instanceof PublicKey
      ? lpMintPubkey
      : new PublicKey(lpMintPubkey);
  const [pda, bump] = PublicKey.findProgramAddressSync(
    [Buffer.from("lock_record"), owner.toBuffer(), mint.toBuffer()],
    program,
  );
  return { pda, bump };
}

function readU64LE(buf, offset) {
  const lo = buf.readUInt32LE(offset);
  const hi = buf.readUInt32LE(offset + 4);
  return BigInt(lo) + (BigInt(hi) << 32n);
}

function readI64LE(buf, offset) {
  const u = readU64LE(buf, offset);
  // interpret as signed
  if (u >= 0x8000000000000000n) {
    return Number(u - 0x10000000000000000n);
  }
  return Number(u);
}

/**
 * Decode LockRecord account data (supports old 89-byte and current 162-byte layouts).
 */
export function decodeLockRecord(data, pda) {
  const buf = Buffer.from(data);
  if (buf.length < OLD_LOCK_RECORD_LEN) return null;
  if (!buf.subarray(0, 8).equals(LOCK_RECORD_DISC)) return null;

  const owner = new PublicKey(buf.subarray(8, 40)).toBase58();
  const lpMint = new PublicKey(buf.subarray(40, 72)).toBase58();
  const amount = readU64LE(buf, 72).toString();
  const unlockTime = readI64LE(buf, 80);
  const bump = buf[88];

  let lockType = 0;
  let targetPriceE9 = "0";
  let priceBaseMint = "";
  let priceQuoteMint = "";

  let hasReceivers = false;
  if (buf.length >= NEW_LOCK_RECORD_LEN) {
    const lockTypeRaw = buf[89];
    hasReceivers = (lockTypeRaw & 0x80) !== 0;
    lockType = lockTypeRaw & 0x7f;
    targetPriceE9 = readU64LE(buf, 90).toString();
    priceBaseMint = new PublicKey(buf.subarray(98, 130)).toBase58();
    priceQuoteMint = new PublicKey(buf.subarray(130, 162)).toBase58();
  }

  const nowSec = Math.floor(Date.now() / 1000);
  const expired = unlockTime !== 0 && unlockTime <= nowSec;
  let isUnlockable = false;
  if (lockType === LOCK_TYPE_TIME) isUnlockable = unlockTime <= nowSec;
  else if (lockType === LOCK_TYPE_CLIFFS) isUnlockable = false;
  else isUnlockable = expired;

  return {
    pda: typeof pda === "string" ? pda : pda.toBase58(),
    owner,
    lpMint,
    amount,
    unlockTime,
    bump,
    lockType,
    hasReceivers,
    targetPriceE9,
    priceBaseMint,
    priceQuoteMint,
    isUnlockable,
    isExpired: expired,
    layout: buf.length >= NEW_LOCK_RECORD_LEN ? "v2" : "v1",
    dataLen: buf.length,
  };
}

/**
 * Fetch all lock records owned by `ownerPubkey` from the locker program.
 * Prefer getProgramAccounts, then always merge a PDA scan over wallet token mints.
 * Browser RPC proxies often return an empty GPA result even when the lock exists.
 */
export async function fetchOwnerLocks(connection, programId, ownerPubkey) {
  if (!connection || !programId || !ownerPubkey) return [];

  const owner =
    ownerPubkey instanceof PublicKey
      ? ownerPubkey
      : new PublicKey(ownerPubkey);
  const program =
    programId instanceof PublicKey ? programId : new PublicKey(programId);

  const byPda = new Map();

  try {
    const accounts = await connection.getProgramAccounts(program, {
      commitment: "confirmed",
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: bs58.encode(LOCK_RECORD_DISC),
          },
        },
        {
          memcmp: {
            offset: 8,
            bytes: owner.toBase58(),
          },
        },
      ],
    });
    for (const row of accounts) {
      byPda.set(row.pubkey.toBase58(), row);
    }
  } catch {
    // GPA is blocked or rate-limited on some RPC proxies.
  }

  try {
    const scanned = await fetchLockAccountsByMintScan(connection, program, owner);
    for (const row of scanned) {
      byPda.set(row.pubkey.toBase58(), row);
    }
  } catch {
    // token-account scan is best-effort
  }

  const locks = [];
  for (const { pubkey, account } of byPda.values()) {
    try {
      const decoded = decodeLockRecord(account.data, pubkey);
      if (decoded) locks.push(decoded);
    } catch {
      // skip undecodable
    }
  }

  const cliffLocks = locks.filter((l) => Number(l.lockType) === LOCK_TYPE_CLIFFS);
  if (cliffLocks.length > 0) {
    const pdas = cliffLocks.map(
      (l) => deriveCliffSchedulePda(program, l.pda).pda,
    );
    const infos = await connection.getMultipleAccountsInfo(pdas);
    cliffLocks.forEach((lock, i) => {
      attachCliffSchedule(lock, pdas[i], infos[i]);
    });
  }

  await attachReceivers(connection, program, locks, owner.toBase58());

  try {
    const asReceiver = await fetchLocksAsReceiver(
      connection,
      program,
      owner.toBase58(),
      new Set(locks.map((l) => l.pda)),
    );
    locks.push(...asReceiver);
  } catch {
    // receiver scan uses GPA; ignore if the proxy rejects it
  }

  locks.sort((a, b) => (b.unlockTime || 0) - (a.unlockTime || 0));
  return locks;
}

async function fetchLockAccountsByMintScan(connection, program, owner) {
  const [spl, t22] = await Promise.all([
    connection
      .getParsedTokenAccountsByOwner(owner, { programId: TOKEN_PROGRAM_ID })
      .catch(() => ({ value: [] })),
    connection
      .getParsedTokenAccountsByOwner(owner, {
        programId: TOKEN_2022_PROGRAM_ID,
      })
      .catch(() => ({ value: [] })),
  ]);
  const mints = new Set();
  for (const { account } of [...(spl.value || []), ...(t22.value || [])]) {
    const mint = account?.data?.parsed?.info?.mint;
    if (mint) mints.add(mint);
  }
  const pdas = [...mints].map(
    (mint) => deriveLockRecordPda(program, owner, mint).pda,
  );
  const found = [];
  for (let i = 0; i < pdas.length; i += 100) {
    const chunk = pdas.slice(i, i + 100);
    const infos = await connection.getMultipleAccountsInfo(chunk);
    infos.forEach((info, j) => {
      if (!info?.data) return;
      found.push({ pubkey: chunk[j], account: info });
    });
  }
  return found;
}

async function attachReceivers(connection, program, locks, walletB58) {
  const flagged = locks.filter((l) => l.hasReceivers);
  if (flagged.length === 0) return;
  const pdas = flagged.map((l) => deriveLockReceiversPda(program, l.pda).pda);
  const infos = await connection.getMultipleAccountsInfo(pdas);
  flagged.forEach((lock, i) => {
    applyReceiversToLock(lock, pdas[i], infos[i], walletB58);
  });
}

async function fetchLocksAsReceiver(
  connection,
  program,
  walletB58,
  alreadyHave,
) {
  const accounts = await connection.getProgramAccounts(program, {
    commitment: "confirmed",
    filters: [
      {
        memcmp: {
          offset: 0,
          bytes: bs58.encode(LOCK_RECEIVERS_DISC),
        },
      },
    ],
  });
  const extra = [];
  for (const { pubkey, account } of accounts) {
    const rec = decodeLockReceivers(account.data, pubkey);
    if (!rec || rec.wallets.indexOf(walletB58) < 0) continue;
    if (alreadyHave.has(rec.lockRecord)) continue;
    const info = await connection.getAccountInfo(new PublicKey(rec.lockRecord));
    if (!info?.data) continue;
    const decoded = decodeLockRecord(info.data, rec.lockRecord);
    if (!decoded) continue;
    if (Number(decoded.lockType) === LOCK_TYPE_CLIFFS) {
      const { pda } = deriveCliffSchedulePda(program, rec.lockRecord);
      const cliffInfo = await connection.getAccountInfo(pda);
      attachCliffSchedule(decoded, pda, cliffInfo);
    }
    applyReceiversToLock(decoded, pubkey, account, walletB58);
    extra.push(decoded);
  }
  return extra;
}

function applyReceiversToLock(lock, pda, accountInfo, walletB58) {
  lock.receiversPda = typeof pda === "string" ? pda : pda.toBase58();
  if (!accountInfo?.data) {
    lock.receivers = [];
    lock.receiverRole = lock.owner === walletB58 ? "owner" : "none";
    return;
  }
  const rec = decodeLockReceivers(accountInfo.data, pda);
  if (!rec) {
    lock.receivers = [];
    return;
  }
  lock.hasReceivers = true;
  lock.receivers = rec.wallets;
  lock.receiverClaimed = rec.claimed;
  lock.receiversOriginal = rec.originalAmount;
  const idx = rec.wallets.indexOf(walletB58);
  lock.receiverIndex = idx;
  lock.receiverRole =
    idx >= 0 ? (lock.owner === walletB58 ? "owner-receiver" : "receiver") : "owner";
  if (idx >= 0) {
    const share = receiverShareRaw(rec.originalAmount, rec.count, idx);
    const claimed = BigInt(rec.claimed[idx] || "0");
    lock.myShare = share.toString();
    lock.myClaimed = claimed.toString();
    const remainingShare = share > claimed ? share - claimed : 0n;
    if (Number(lock.lockType) === LOCK_TYPE_CLIFFS) {
      const vestedTotal =
        BigInt(lock.withdrawn || "0") + BigInt(lock.claimable || "0");
      const vestedShare = receiverShareRaw(
        vestedTotal.toString(),
        rec.count,
        idx,
      );
      const due = vestedShare > claimed ? vestedShare - claimed : 0n;
      lock.claimable = due.toString();
      lock.isUnlockable = due > 0n;
    } else {
      lock.claimable = remainingShare.toString();
      lock.isUnlockable = remainingShare > 0n && Boolean(lock.isUnlockable);
    }
  }
}

export async function fetchLockReceivers(connection, programId, lockRecordPda) {
  if (!connection || !programId || !lockRecordPda) return null;
  const { pda } = deriveLockReceiversPda(programId, lockRecordPda);
  const info = await connection.getAccountInfo(pda);
  if (!info?.data) return null;
  return decodeLockReceivers(info.data, pda);
}

function attachCliffSchedule(lock, pda, accountInfo) {
  if (!accountInfo?.data) {
    lock.cliffs = [];
    lock.originalAmount = lock.amount;
    lock.withdrawn = "0";
    lock.claimable = "0";
    lock.cliffSchedulePda = pda.toBase58();
    lock.isUnlockable = false;
    return;
  }
  const decoded = decodeCliffSchedule(accountInfo.data);
  if (!decoded) {
    lock.cliffs = [];
    lock.cliffSchedulePda = pda.toBase58();
    lock.isUnlockable = false;
    return;
  }
  const claimable = claimableRaw(
    decoded.originalAmount,
    decoded.withdrawn,
    decoded.cliffs,
  );
  lock.cliffs = decoded.cliffs;
  lock.originalAmount = decoded.originalAmount;
  lock.withdrawn = decoded.withdrawn;
  lock.claimable = claimable.toString();
  lock.cliffSchedulePda = pda.toBase58();
  lock.firstUnlockTime = decoded.cliffs[0]?.unlockTime || lock.unlockTime;
  lock.nextUnlockTime = nextCliffTime(decoded.cliffs);
  lock.isUnlockable = claimable > 0n;
}

export async function fetchCliffSchedule(connection, programId, lockRecordPda) {
  if (!connection || !programId || !lockRecordPda) return null;
  const { pda } = deriveCliffSchedulePda(programId, lockRecordPda);
  const info = await connection.getAccountInfo(pda);
  if (!info?.data) return null;
  const decoded = decodeCliffSchedule(info.data);
  if (!decoded) return null;
  return { pda: pda.toBase58(), ...decoded };
}

/**
 * Best-effort decimals lookup for display.
 */
export async function fetchMintDecimals(connection, mintAddress) {
  try {
    const info = await connection.getParsedAccountInfo(new PublicKey(mintAddress));
    return info?.value?.data?.parsed?.info?.decimals ?? 6;
  } catch {
    return 6;
  }
}

export function formatRawAmount(amountStr, decimals) {
  try {
    const raw = BigInt(amountStr);
    if (decimals === 0) return raw.toString();
    const base = 10n ** BigInt(decimals);
    const whole = raw / base;
    const frac = raw % base;
    if (frac === 0n) return whole.toString();
    const fracStr = frac.toString().padStart(decimals, "0").replace(/0+$/, "");
    return `${whole}.${fracStr}`;
  } catch {
    return amountStr;
  }
}
